import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { API, authFetch } from "@/App";
import { toast } from "sonner";
import { MilestonesPanel } from "./MilestonesPanel";
import { GanttChart } from "./GanttChart";
import {
  Plus, Loader2, Sparkles, Send, FileText, Trash2, Mail, Phone, Building2, CalendarDays
} from "lucide-react";

const STATUS_STYLES = {
  active: "bg-green-100 text-green-700",
  on_hold: "bg-yellow-100 text-yellow-700",
  completed: "bg-blue-100 text-blue-700"
};

export function ProjectDetailDialog({ open, onOpenChange, project, setProject, clients, companies, onRefresh }) {
  const [progressForm, setProgressForm] = useState({
    update_text: "", tasks_completed: "", tasks_upcoming: "", blockers: "", percentage_complete: 0
  });
  const [reportContent, setReportContent] = useState("");
  const [aiGenerating, setAiGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [localNotes, setLocalNotes] = useState("");
  const [localGoals, setLocalGoals] = useState("");

  useEffect(() => {
    if (project) {
      setLocalNotes(project.notes || "");
      setLocalGoals(project.goals || "");
      setProgressForm(prev => ({ ...prev, percentage_complete: project.percentage_complete || 0 }));
    }
  }, [project?.project_id]);

  const handleUpdateProject = async (field, value, showToast = true) => {
    if (!project) return;
    try {
      const response = await authFetch(`${API}/consultant/projects/${project.project_id}`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ [field]: value })
      });
      if (response.ok) {
        setProject(prev => ({ ...prev, [field]: value }));
        if (field === 'status' || field === 'percentage_complete' || field === 'company_id') onRefresh();
        if (showToast) toast.success("Project updated");
      }
    } catch (_error) {
      toast.error("Failed to update");
    }
  };

  const handleAssignClient = async (clientId) => {
    if (!project) return;
    try {
      const res = await authFetch(`${API}/consultant/projects/${project.project_id}/assign-client`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ client_id: clientId })
      });
      if (res.ok) {
        const client = clients.find(c => c.user_id === clientId);
        setProject(prev => ({ ...prev, client_id: clientId, client: client || prev.client }));
        onRefresh();
        toast.success("Client assigned");
      } else {
        const e = await res.json().catch(() => ({}));
        toast.error(e.detail || "Failed");
      }
    } catch (e) { toast.error(`Error: ${e.message}`); }
  };

  const handleDeleteProject = async () => {
    if (!window.confirm(`Delete project "${project.title}"? This cannot be undone.`)) return;
    try {
      const res = await authFetch(`${API}/consultant/projects/${project.project_id}`, { method: "DELETE" });
      if (res.ok) { toast.success("Project deleted"); onOpenChange(false); setProject(null); onRefresh(); }
      else { const e = await res.json().catch(() => ({})); toast.error(e.detail || "Failed to delete"); }
    } catch (e) { toast.error(`Error: ${e.message}`); }
  };

  const handleSaveNotes = async () => {
    if (localNotes !== (project?.notes || "")) await handleUpdateProject("notes", localNotes, true);
  };

  const handleSaveGoals = async () => {
    if (localGoals !== (project?.goals || "")) await handleUpdateProject("goals", localGoals, true);
  };

  const handleAddProgressUpdate = async () => {
    if (!progressForm.update_text) { toast.error("Please enter an update"); return; }
    setSaving(true);
    try {
      const response = await authFetch(`${API}/consultant/projects/${project.project_id}/progress`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...progressForm,
          tasks_completed: progressForm.tasks_completed.split("\n").filter(t => t.trim()),
          tasks_upcoming: progressForm.tasks_upcoming.split("\n").filter(t => t.trim())
        })
      });
      if (response.ok) {
        toast.success("Progress update added!");
        setProgressForm({ update_text: "", tasks_completed: "", tasks_upcoming: "", blockers: "", percentage_complete: project.percentage_complete });
        const detailRes = await authFetch(`${API}/consultant/projects/${project.project_id}`);
        if (detailRes.ok) setProject(await detailRes.json());
        onRefresh();
      }
    } catch (_error) { toast.error("Failed to add update"); }
    finally { setSaving(false); }
  };

  const handleAiGenerateReport = async () => {
    setAiGenerating(true);
    try {
      const response = await authFetch(`${API}/ai/project/progress-report`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ project_id: project.project_id })
      });
      if (response.ok) { const data = await response.json(); setReportContent(data.report); toast.success("Report generated!"); }
      else toast.error("Failed to generate report");
    } catch (_error) { toast.error("AI generation failed"); }
    finally { setAiGenerating(false); }
  };

  const handleSendReport = async () => {
    if (!reportContent) { toast.error("Please generate or write a report first"); return; }
    setSaving(true);
    try {
      const response = await authFetch(`${API}/consultant/projects/${project.project_id}/send-report`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ report_content: reportContent })
      });
      if (response.ok) { toast.success("Progress report sent to client!"); setReportContent(""); }
      else toast.error("Failed to send report");
    } catch (_error) { toast.error("Failed to send report"); }
    finally { setSaving(false); }
  };

  const handleAiUpdateNotes = async () => {
    setAiGenerating(true);
    try {
      const response = await authFetch(`${API}/ai/project/update-notes`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ project_id: project.project_id, new_info: progressForm.update_text || "Recent progress updates" })
      });
      if (response.ok) {
        const data = await response.json();
        setLocalNotes(data.updated_notes);
        handleUpdateProject("notes", data.updated_notes, false);
        toast.success("Notes updated with AI assistance!");
      }
    } catch (_error) { toast.error("Failed to update notes"); }
    finally { setAiGenerating(false); }
  };

  const getStatusBadge = (status) => (
    <Badge className={STATUS_STYLES[status] || "bg-gray-100"}>{status?.replace("_", " ").toUpperCase()}</Badge>
  );

  if (!project) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle className="text-xl">{project.title}</DialogTitle>
              <p className="text-sm text-neutral-500 mt-1">
                {project.client?.name} &middot; {project.project_type}
                {project.company_name && <> &middot; <span className="text-blue-600">{project.company_name}</span></>}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {getStatusBadge(project.status)}
              <Select value={project.status} onValueChange={v => handleUpdateProject("status", v)}>
                <SelectTrigger className="w-28 h-8 text-xs" data-testid="detail-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="on_hold">On Hold</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
              <Button size="sm" variant="outline" className="h-8 text-red-500 hover:text-red-600 hover:bg-red-50" data-testid="delete-project-btn"
                onClick={handleDeleteProject}>
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        </DialogHeader>

        <Tabs defaultValue="overview" className="mt-4">
          <TabsList className="grid grid-cols-6 w-full">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="milestones" data-testid="milestones-tab">Milestones</TabsTrigger>
            <TabsTrigger value="timeline" data-testid="timeline-tab">
              <CalendarDays className="w-3.5 h-3.5 mr-1" />Timeline
            </TabsTrigger>
            <TabsTrigger value="progress">Progress</TabsTrigger>
            <TabsTrigger value="notes">Notes</TabsTrigger>
            <TabsTrigger value="report">Send Report</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-4 mt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Card className="border-blue-100 bg-blue-50/50">
                <CardContent className="py-3 px-4">
                  <Label className="text-xs text-blue-600 font-semibold mb-2 block">Direct Contact</Label>
                  <Select value={project.client_id || "none"} onValueChange={v => handleAssignClient(v === "none" ? "" : v)}>
                    <SelectTrigger className="h-8 text-sm mb-2" data-testid="detail-client-select"><SelectValue placeholder="Assign a client" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No client assigned</SelectItem>
                      {clients.map(c => <SelectItem key={c.user_id} value={c.user_id}>{c.name} ({c.email})</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {project.client?.name && <p className="text-sm font-medium">{project.client.name}</p>}
                  {project.client?.email && (
                    <div className="flex items-center gap-1.5 text-xs text-neutral-600 mt-1">
                      <Mail className="w-3 h-3 text-blue-500" />
                      <a href={`mailto:${project.client.email}`} className="hover:text-emerald-600 hover:underline">{project.client.email}</a>
                    </div>
                  )}
                  {project.client?.phone && (
                    <div className="flex items-center gap-1.5 text-xs text-neutral-600 mt-1">
                      <Phone className="w-3 h-3 text-blue-500" />
                      <a href={`tel:${project.client.phone}`} className="hover:text-emerald-600 hover:underline">{project.client.phone}</a>
                    </div>
                  )}
                  {project.client?.company && <p className="text-xs text-neutral-400 mt-1">{project.client.company}</p>}
                </CardContent>
              </Card>
              <Card className="border-neutral-100">
                <CardContent className="py-3 px-4">
                  <Label className="text-xs text-neutral-500 font-semibold mb-2 block">Company</Label>
                  <Select
                    value={project.company_id || "none"}
                    onValueChange={(v) => {
                      const companyId = v === "none" ? "" : v;
                      const companyName = companies.find(c => c.company_id === companyId)?.name || "";
                      handleUpdateProject("company_id", companyId);
                      handleUpdateProject("company_name", companyName, false);
                      setProject(prev => ({ ...prev, company_id: companyId, company_name: companyName }));
                    }}
                  >
                    <SelectTrigger className="h-8 text-sm" data-testid="detail-company-select"><SelectValue placeholder="No company" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No company</SelectItem>
                      {companies.map(c => <SelectItem key={c.company_id} value={c.company_id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {project.company_name && (
                    <p className="text-xs text-blue-600 mt-1.5 flex items-center gap-1">
                      <Building2 className="w-3 h-3" />{project.company_name}
                    </p>
                  )}
                </CardContent>
              </Card>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-neutral-500">Status</Label>
                <Select value={project.status} onValueChange={(v) => handleUpdateProject("status", v)}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="on_hold">On Hold</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-neutral-500">Progress</Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input type="number" min="0" max="100" value={project.percentage_complete || 0}
                    onChange={(e) => handleUpdateProject("percentage_complete", parseInt(e.target.value) || 0)} className="w-20" />
                  <span className="text-neutral-500">%</span>
                  <Progress value={project.percentage_complete || 0} className="flex-1 h-3" />
                </div>
              </div>
            </div>

            <div>
              <Label className="text-neutral-500">Goals</Label>
              <Textarea value={localGoals} onChange={(e) => setLocalGoals(e.target.value)} onBlur={handleSaveGoals}
                className="mt-1 min-h-24" placeholder="Project goals..." />
              <p className="text-xs text-neutral-400 mt-1">Changes save automatically when you click away</p>
            </div>

            <div className="text-sm text-neutral-500">Created: {new Date(project.created_at).toLocaleDateString()}</div>
          </TabsContent>

          <TabsContent value="milestones" className="space-y-4 mt-4">
            <MilestonesPanel
              project={project}
              onUpdate={(updated) => {
                setProject(prev => ({ ...prev, milestones: updated.milestones, percentage_complete: updated.percentage_complete }));
                onRefresh();
              }}
            />
          </TabsContent>

          <TabsContent value="timeline" className="space-y-4 mt-4">
            <GanttChart
              project={project}
              onMilestoneClick={() => {
                /* Switch to milestones tab for editing - handled by parent tab control */
              }}
            />
          </TabsContent>

          <TabsContent value="progress" className="space-y-4 mt-4">
            <Card className="border-emerald-200 bg-emerald-50">
              <CardHeader className="pb-3"><CardTitle className="text-base">Add Progress Update</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <Textarea value={progressForm.update_text} onChange={(e) => setProgressForm(prev => ({ ...prev, update_text: e.target.value }))}
                  placeholder="What progress was made?" className="min-h-20" />
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-sm">Tasks Completed (one per line)</Label>
                    <Textarea value={progressForm.tasks_completed} onChange={(e) => setProgressForm(prev => ({ ...prev, tasks_completed: e.target.value }))}
                      placeholder={"Task 1\nTask 2"} className="min-h-16 mt-1" />
                  </div>
                  <div>
                    <Label className="text-sm">Upcoming Tasks (one per line)</Label>
                    <Textarea value={progressForm.tasks_upcoming} onChange={(e) => setProgressForm(prev => ({ ...prev, tasks_upcoming: e.target.value }))}
                      placeholder={"Task 1\nTask 2"} className="min-h-16 mt-1" />
                  </div>
                </div>
                <div className="flex gap-3 items-end">
                  <div className="flex-1">
                    <Label className="text-sm">Blockers</Label>
                    <Input value={progressForm.blockers} onChange={(e) => setProgressForm(prev => ({ ...prev, blockers: e.target.value }))}
                      placeholder="Any blockers?" className="mt-1" />
                  </div>
                  <div className="w-32">
                    <Label className="text-sm">Progress %</Label>
                    <Input type="number" min="0" max="100" value={progressForm.percentage_complete}
                      onChange={(e) => setProgressForm(prev => ({ ...prev, percentage_complete: parseInt(e.target.value) || 0 }))} className="mt-1" />
                  </div>
                </div>
                <Button onClick={handleAddProgressUpdate} disabled={saving} className="w-full">
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                  Add Update
                </Button>
              </CardContent>
            </Card>

            <div className="space-y-3">
              <h4 className="font-medium">Update History</h4>
              {project.progress_updates?.length > 0 ? (
                [...project.progress_updates].reverse().map((update, idx) => (
                  <Card key={update.update_id || idx} className="border-neutral-200">
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start mb-2">
                        <span className="text-sm text-neutral-500">{new Date(update.created_at).toLocaleString()}</span>
                        <Badge variant="outline">{update.percentage_complete}%</Badge>
                      </div>
                      <p className="text-neutral-700">{update.update_text}</p>
                      {update.tasks_completed?.length > 0 && (
                        <div className="mt-2">
                          <p className="text-xs text-green-600 font-medium">Completed:</p>
                          <ul className="text-sm text-neutral-600 list-disc list-inside">
                            {update.tasks_completed.map((t, i) => <li key={i}>{t}</li>)}
                          </ul>
                        </div>
                      )}
                      {update.blockers && <p className="text-sm text-red-600 mt-2">Blocker: {update.blockers}</p>}
                    </CardContent>
                  </Card>
                ))
              ) : (
                <p className="text-neutral-500 text-center py-4">No updates yet</p>
              )}
            </div>
          </TabsContent>

          <TabsContent value="notes" className="space-y-4 mt-4">
            <div className="flex justify-end">
              <Button variant="outline" size="sm" onClick={handleAiUpdateNotes} disabled={aiGenerating}>
                {aiGenerating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                AI Organize Notes
              </Button>
            </div>
            <Textarea value={localNotes} onChange={(e) => setLocalNotes(e.target.value)} onBlur={handleSaveNotes}
              className="min-h-64" placeholder="Project notes, details, client preferences..." />
            <p className="text-xs text-neutral-400">Changes save automatically when you click away</p>
          </TabsContent>

          <TabsContent value="report" className="space-y-4 mt-4">
            <Card className="border-purple-200 bg-purple-50">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">Generate Progress Report</CardTitle>
                  <Button onClick={handleAiGenerateReport} disabled={aiGenerating} className="bg-gradient-to-r from-purple-600 to-indigo-600">
                    {aiGenerating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                    AI Generate
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <Textarea value={reportContent} onChange={(e) => setReportContent(e.target.value)}
                  placeholder="Report content will appear here. You can edit before sending." className="min-h-48 bg-white" />
                <Button onClick={handleSendReport} disabled={saving || !reportContent} className="w-full mt-3 bg-emerald-600 hover:bg-emerald-700">
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                  Send Report to {project.client?.name}
                </Button>
                <p className="text-xs text-neutral-500 mt-2 text-center">Report will be sent to {project.client?.email}</p>
              </CardContent>
            </Card>

            {project.reports_sent?.length > 0 && (
              <div>
                <h4 className="font-medium mb-2">Reports Sent</h4>
                <div className="space-y-2">
                  {project.reports_sent.map((report, idx) => (
                    <div key={idx} className="text-sm text-neutral-600 flex items-center">
                      <FileText className="w-4 h-4 mr-2 text-neutral-400" />
                      Sent on {new Date(report.sent_at).toLocaleString()}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
